import { Controller } from "@hotwired/stimulus";

// GR-3426: the guest room block's night strip on wizard step 2.
//
// Every night the block defaults to is server-rendered, so the strip renders
// and posts without this controller; it adds the running totals, the Match day
// link and the two "add a night" buttons. A night it adds is cloned from the
// same <template> the server rendered a cell into, the way the quote form's
// Add Line row is.
//
// GR-3470: it is not, however, a working no-JS surface, and the original
// comment here claimed it was. Since GR-3448 the hidden `custom` field is what
// records that a night has stopped following its day, and only countEdited
// sets it, so a count typed with JS off onto a night that a day covers is put
// back to that day's attendance on save. That is deliberate, and pinned by "a
// count posted without the custom flag goes back in step with its day" in
// event_request_advanced_schedule_builder_test: the flag is the only thing
// that can tell a deliberate figure from a stale one. Supporting the typing
// without JS would mean giving the server another way to tell them apart, not
// changing anything here.
//
// GR-3304: the block is a grid of room types by nights, mirroring the Room
// Types table in getregistered rooming. A row per type the requester names
// (the standard row is the only one that follows attendance), a column per
// night. Rows are added from the dashed row at the foot, deleted, and
// reordered by their handle; nights are added by the round plus buttons in the
// first and last night's headers. Each type posts its nights under it.
export default class EventRequestRoomBlockController extends Controller {
  static targets = [
    "headRow",
    "night",
    "typeRows",
    "typeRow",
    "newTypeRow",
    "typeName",
    "position",
    "cell",
    "count",
    "custom",
    "resync",
    "rowTotal",
    "nightTotal",
    "grandTotal",
    "total",
    "peak",
    "addBefore",
    "addAfter",
    "nightTemplate",
    "standardCellTemplate",
    "cellTemplate",
    "nightTotalTemplate",
    "typeTemplate",
  ];

  // minDate/maxDate are RoomNight's validation window: past them the server
  // refuses the row, so the buttons stop offering it. firstDay/lastDay are the
  // meeting's own dates, which is what names a night.
  static values = {
    minDate: String,
    maxDate: String,
    firstDay: String,
    lastDay: String,
  };

  connect() {
    this.newTypeRowTargets.forEach((row) => this.refreshPending(row));
    this.refreshResync();
    this.recalculate();
    this.refreshAddButtons();

    // GR-3304 review: counts on a type with no name are refused here, before
    // the form is sent. Captured, so the wizard's own submit handling never
    // sees a submit this cancels.
    this.form = this.element.closest("form");
    this.guard = (event) => this.guardSubmit(event);
    this.form?.addEventListener("submit", this.guard, true);
  }

  disconnect() {
    this.form?.removeEventListener("submit", this.guard, true);
  }

  // ==========================================================================
  // Counts, and GR-3448's attendance sync on the standard row
  // ==========================================================================

  // GR-3448: the requester typed in a standard night, so that night has
  // stopped following its day. The hidden flag is what tells the server, and
  // the Match day link is the way back. Any other type follows nothing.
  countEdited(event) {
    const cell = event.target.closest("[data-event-request-room-block-target='cell']");
    if (this.isStandardCell(cell)) this.markCustom(cell, true);
    this.recalculate();
  }

  // GR-3448: back to following the day, at whatever the Attendance box says
  // right now rather than what it said when the page was rendered. Board H1
  // makes it one link in the Standard row's caption for every night typed
  // over, rather than a link in each cell. A night no day covers follows
  // nothing, and stays as it is.
  resyncToDays() {
    this.matchableCells().forEach((cell) => {
      const capacity = this.dayCapacity(cell.dataset.coversDay);
      if (capacity !== null) this.countInput(cell).value = capacity;
      this.markCustom(cell, false);
    });
    this.recalculate();
  }

  matchableCells() {
    return this.standardCells().filter((cell) => cell.dataset.coversDay && this.isCustom(cell));
  }

  // The caption's link is offered only while there is a night to match.
  refreshResync() {
    if (this.hasResyncTarget) this.resyncTarget.hidden = this.matchableCells().length === 0;
  }

  // GR-3448: a day's attendance moved. Every standard night that still
  // follows that day moves with it, before anything is saved, so the grid and
  // its figures never disagree with the programme above them.
  attendanceChanged(event) {
    const { date, capacity } = event.detail ?? {};
    if (!date) return;

    this.standardCells().forEach((cell) => {
      if (cell.dataset.coversDay !== date) return;
      if (this.isCustom(cell)) return;

      this.countInput(cell).value = capacity;
    });

    this.recalculate();
  }

  isCustom(cell) {
    return this.customInput(cell)?.value === "1";
  }

  markCustom(cell, custom) {
    if (!cell) return;

    const flag = this.customInput(cell);
    if (flag) flag.value = custom ? "1" : "0";
    cell.classList.toggle("room-block-grid__cell--custom", custom);
    this.refreshResync();
  }

  customInput(cell) {
    return cell?.querySelector("input[name$='[custom]']");
  }

  // The Attendance box of a given day, read live out of the programme table
  // above. Returns null when the day is not on the page.
  dayCapacity(dayDate) {
    if (!dayDate) return null;

    const dateField = Array.from(
      document.querySelectorAll("input[name^='event_request[days_attributes]'][name$='[date]']")
    ).find((field) => field.value === dayDate);

    return dateField?.closest("[data-event-request-schedule-target='dayRow']")
      ?.querySelector("input[name$='[capacity]']")?.value ?? null;
  }

  // Every figure in one walk: each row's total, each night's total, the
  // grand total, and the header's two (the total is what the block is billed
  // as, the peak is what the hotel has to hold on its busiest night).
  recalculate() {
    const byNight = new Map(this.liveNights().map((night) => [night.dataset.date, 0]));

    this.liveRows().forEach((row) => {
      let rowTotal = 0;
      this.rowCells(row).forEach((cell) => {
        if (cell.hidden || !byNight.has(cell.dataset.date)) return;

        const rooms = this.rooms(this.countInput(cell));
        rowTotal += rooms;
        byNight.set(cell.dataset.date, byNight.get(cell.dataset.date) + rooms);
      });
      const figure = row.querySelector("[data-event-request-room-block-target='rowTotal']");
      if (figure) figure.textContent = String(rowTotal);
    });

    // The pending row shows what it would add, without adding it yet.
    this.newTypeRowTargets.forEach((row) => {
      const sum = this.rowCells(row).reduce((total, cell) => total + (cell.hidden ? 0 : this.rooms(this.countInput(cell))), 0);
      const figure = row.querySelector("[data-event-request-room-block-target='rowTotal']");
      if (figure) figure.textContent = String(sum);
    });

    const nightly = Array.from(byNight.values());
    const total = nightly.reduce((sum, rooms) => sum + rooms, 0);
    const peak = nightly.reduce((max, rooms) => Math.max(max, rooms), 0);

    this.nightTotalTargets.forEach((figure) => {
      if (byNight.has(figure.dataset.date)) figure.textContent = String(byNight.get(figure.dataset.date));
    });
    if (this.hasGrandTotalTarget) this.grandTotalTarget.textContent = String(total);
    if (this.hasTotalTarget) this.totalTarget.textContent = String(total);
    if (this.hasPeakTarget) this.peakTarget.textContent = String(peak);
  }

  // An empty or nonsense box is nothing, never NaN.
  rooms(input) {
    const rooms = parseInt(input?.value, 10);
    return Number.isFinite(rooms) && rooms >= 0 ? rooms : 0;
  }

  // ==========================================================================
  // Nights (columns)
  // ==========================================================================

  addBefore() {
    this.addNight(this.edgeDate(-1), true);
  }

  addAfter() {
    this.addNight(this.edgeDate(1), false);
  }

  // A night already in the grid but marked for removal is restored rather
  // than added again: two rows for one date and type would collide on the
  // unique index the moment the destroy and the create were saved together.
  addNight(date, atStart) {
    if (!date || !this.withinWindow(date)) return;

    const removed = this.nightTargets.find((night) => night.dataset.date === date && night.hidden);

    if (removed) {
      this.restoreNight(removed, atStart);
    } else {
      const index = this.nextNightIndex();
      const header = this.fill(this.nightTemplateTarget, { index, date });
      // Its X points inwards: a night added after the programme has it on
      // the left of its header.
      header.classList.toggle("room-block-grid__night--trailing", !atStart);
      this.insertAtEdge(this.headRowTarget, this.nightTargets, header, atStart);

      this.allRows().forEach((row) => this.addCell(row, date, index, atStart));
      this.newTypeRowTargets.forEach((row) => this.refreshPending(row));

      const foot = this.fill(this.nightTotalTemplateTarget, { index, date });
      this.insertAtEdge(this.footRow(), this.nightTotalTargets, foot, atStart);
    }

    this.recalculate();
    this.refreshAddButtons();
  }

  // A night with a saved row on any type is hidden and its saved rows marked
  // for destruction, so the server hears about it; an unsaved one leaves the
  // DOM. The add buttons are lifted out first, since one may sit in its
  // header.
  removeNight(event) {
    const header = event.target.closest("[data-event-request-room-block-target='night']");
    if (!header) return;

    this.parkAddButtons();
    const date = header.dataset.date;
    const cells = this.cellTargets.filter((cell) => cell.dataset.date === date);
    const saved = cells.some((cell) => this.savedRow(cell));
    const foot = this.nightTotalTargets.filter((figure) => figure.dataset.date === date);

    if (saved) {
      cells.forEach((cell) => this.retire(cell));
      [header, ...foot].forEach((element) => {
        element.hidden = true;
      });
    } else {
      [header, ...cells, ...foot].forEach((element) => element.remove());
    }

    this.recalculate();
    this.refreshAddButtons();
  }

  restoreNight(header, atStart) {
    const date = header.dataset.date;
    header.hidden = false;
    this.nightTotalTargets
      .filter((figure) => figure.dataset.date === date)
      .forEach((figure) => {
        figure.hidden = false;
      });

    this.allRows().forEach((row) => {
      const cell = this.rowCells(row).find((each) => each.dataset.date === date);
      if (cell) {
        this.revive(cell);
      } else {
        this.addCell(row, date, header.dataset.index, atStart);
      }
    });
    this.newTypeRowTargets.forEach((row) => this.refreshPending(row));
  }

  // One type's cell for a night, cloned from the standard or the plain
  // template and keyed under that type's own index.
  addCell(row, date, index, atStart) {
    const standard = this.isStandardRow(row);
    const template = standard ? this.standardCellTemplateTarget : this.cellTemplateTarget;
    const cell = this.fill(template, {
      index,
      date,
      typeIndex: row.dataset.index,
      typeName: this.typeNameOf(row),
    });

    const cells = this.rowCells(row);
    if (cells.length > 0) {
      cells[atStart ? 0 : cells.length - 1].insertAdjacentElement(atStart ? "beforebegin" : "afterend", cell);
    } else {
      row.querySelector("[data-event-request-room-block-target='rowTotal']")
        ?.insertAdjacentElement("beforebegin", cell);
    }

    // GR-3448: the template's standard cell is custom, which is right for a
    // night beyond the meeting. One the meeting still covers - the night of
    // the final day is the usual case - opens following that day instead.
    if (standard) this.adoptCoveredDay(cell, date);
    return cell;
  }

  // A saved cell is marked for destruction; an unsaved one is left out of the
  // post by disabling its fields.
  retire(cell) {
    cell.hidden = true;
    if (this.savedRow(cell)) {
      const destroy = cell.querySelector("input[name$='[_destroy]']");
      if (destroy) destroy.value = "1";
    } else {
      cell.querySelectorAll("input").forEach((input) => {
        input.disabled = true;
      });
    }
  }

  revive(cell) {
    cell.hidden = false;
    cell.querySelectorAll("input").forEach((input) => {
      input.disabled = false;
    });
    const destroy = cell.querySelector("input[name$='[_destroy]']");
    if (destroy) destroy.value = "0";
  }

  savedRow(cell) {
    return Boolean(cell.querySelector("input[name$='[id]']"));
  }

  // The date a button would add: one day out from whichever end of the grid
  // it sits at. Removed nights are not part of the grid any more, so the
  // range walks back over them.
  edgeDate(step) {
    const dates = this.liveNights()
      .map((night) => night.dataset.date)
      .filter(Boolean)
      .sort();
    if (dates.length === 0) return null;

    return this.shiftDate(step < 0 ? dates[0] : dates[dates.length - 1], step);
  }

  // The round plus buttons live in the first and last night's headers (board
  // A1) and name the night they would add, so they move with the ends.
  refreshAddButtons() {
    const nights = this.liveNights();
    this.placeAddButton(this.hasAddBeforeTarget && this.addBeforeTarget, nights[0], -1, "before");
    this.placeAddButton(this.hasAddAfterTarget && this.addAfterTarget, nights[nights.length - 1], 1, "after");
  }

  placeAddButton(button, header, step, side) {
    if (!button) return;

    const date = this.edgeDate(step);
    button.hidden = !this.canAdd(date);
    if (date) {
      const label = this.dateLabel(date);
      button.setAttribute("aria-label", `Add the night ${side}, ${label}`);
      button.setAttribute("title", `Add ${label}`);
    }

    const holder = header?.querySelector(".room-block-grid__night-head");
    if (!holder) return;
    if (side === "before" && holder.firstElementChild !== button) holder.prepend(button);
    if (side === "after" && holder.lastElementChild !== button) holder.append(button);
  }

  // Keeps the buttons out of a header that is about to leave the DOM.
  parkAddButtons() {
    [this.hasAddBeforeTarget && this.addBeforeTarget, this.hasAddAfterTarget && this.addAfterTarget]
      .filter(Boolean)
      .forEach((button) => this.element.append(button));
  }

  canAdd(date) {
    return Boolean(date) && this.withinWindow(date);
  }

  // ISO dates sort and compare as strings, so no parsing is needed to say
  // whether one is inside the window.
  withinWindow(date) {
    if (!this.minDateValue || !this.maxDateValue) return false;

    return date >= this.minDateValue && date <= this.maxDateValue;
  }

  liveNights() {
    return this.nightTargets.filter((night) => !night.hidden);
  }

  // Index only has to be unique within the submitted params, so keep counting
  // up past nights that have been taken off the grid.
  nextNightIndex() {
    const highest = Math.max(-1, ...this.nightTargets.map((night) => Number(night.dataset.index) || 0));
    this.nightCounter = Math.max(this.nightCounter ?? 0, highest + 1);
    return this.nightCounter++;
  }

  footRow() {
    return this.nightTotalTargets[0]?.closest("tr") ?? this.grandTotalTarget.closest("tr");
  }

  // Puts a new header or footer cell at one end of the run of night cells
  // already in that row, hidden ones included so a restored night keeps its
  // place.
  insertAtEdge(row, existing, element, atStart) {
    const run = existing.filter((each) => row.contains(each));
    if (run.length > 0) {
      run[atStart ? 0 : run.length - 1].insertAdjacentElement(atStart ? "beforebegin" : "afterend", element);
    } else {
      row.lastElementChild.insertAdjacentElement("beforebegin", element);
    }
  }

  // ==========================================================================
  // GR-3304: room types (rows)
  // ==========================================================================

  // The dashed row's plus: the row becomes a real one and a fresh blank row
  // opens under it, with the cursor in its name. The plus is disabled until
  // the row has a name.
  addType(event) {
    const row = event.target.closest("tr");
    if (row && this.commitType(row)) {
      row.nextElementSibling?.querySelector("[data-event-request-room-block-target='typeName']")?.focus();
    }
  }

  // GR-3304 review: a name typed into the pending row makes its counts and
  // its plus usable; on any row it clears the refusal guardSubmit showed.
  typeNameInput(event) {
    const row = event.target.closest("tr");
    if (!row) return;

    if (this.typeNameOf(row)) this.clearNameError(row);
    if (this.isPending(row)) this.refreshPending(row);
  }

  // Leaving the pending row's name with something in it makes it a real
  // row, as the plus does, without taking the cursor anywhere. Not even back
  // to a taken name (review of !4698, finding 10): leaving is the requester
  // moving on, so the name is flagged where it is.
  typeNameBlur(event) {
    const row = event.target.closest("tr");
    if (row && this.isPending(row) && this.typeNameOf(row)) this.commitType(row, { focus: false });
  }

  // The pending row becomes a real one: it loses the dashed style, joins the
  // totals, and a fresh blank row opens under it. A name the block already
  // has would collide on the server, so that is flagged on the box instead,
  // and from the plus the cursor goes back to it.
  commitType(row, { focus = true } = {}) {
    const input = row.querySelector("[data-event-request-room-block-target='typeName']");
    const name = this.typeNameOf(row);
    if (!input || !name) return false;

    const taken = this.liveTypeRows().some((other) => this.typeNameOf(other).toLowerCase() === name.toLowerCase());
    if (taken) {
      this.showNameError(row, `There's already a type called ${name}`);
      if (focus) input.focus();
      return false;
    }

    input.removeAttribute("aria-invalid");
    input.removeAttribute("placeholder");
    row.classList.remove("room-block-grid__type--new");
    row.setAttribute("data-event-request-room-block-target", "typeRow");
    row.querySelector(".room-block-grid__remove-type")?.removeAttribute("hidden");
    row.querySelector(".room-block-grid__add-type")?.setAttribute("hidden", "");
    this.nameRow(row, name);

    const blank = this.fill(this.typeTemplateTarget, {
      typeIndex: this.nextTypeIndex(),
      typeName: "New type",
      position: this.liveTypeRows().length,
    });
    row.insertAdjacentElement("afterend", blank);
    this.liveNights().forEach((night) => this.addCell(blank, night.dataset.date, night.dataset.index, false));
    this.refreshPending(blank);

    this.renumberPositions();
    this.recalculate();
    return true;
  }

  // The pending row's counts are disabled, and so read as disabled, until it
  // has a name; so is its plus. Counts already typed stay usable if the name
  // is cleared again, so the submit can say what is missing rather than
  // quietly dropping them.
  refreshPending(row) {
    const named = Boolean(this.typeNameOf(row));
    const counted = this.rowCells(row).some((cell) => this.rooms(this.countInput(cell)) > 0);
    this.rowCells(row).forEach((cell) => {
      cell.querySelectorAll("input").forEach((input) => {
        input.disabled = !named && !counted;
      });
    });
    const plus = row.querySelector(".room-block-grid__add-type");
    if (plus) plus.disabled = !named;
  }

  isPending(row) {
    return row.getAttribute("data-event-request-room-block-target") === "newTypeRow";
  }

  // GR-3304 review: rooms asked for under no name would be refused by the
  // server; refuse them here, next to the box that is empty.
  guardSubmit(event) {
    const unnamed = [...this.liveTypeRows(), ...this.newTypeRowTargets].filter(
      (row) =>
        !this.typeNameOf(row) &&
        this.rowCells(row).some((cell) => {
          const input = this.countInput(cell);
          return !cell.hidden && !input?.disabled && this.rooms(input) > 0;
        })
    );
    if (unnamed.length === 0) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    unnamed.forEach((row) => this.showNameError(row, "Give this room type a name"));
    unnamed[0].querySelector("[data-event-request-room-block-target='typeName']")?.focus();
  }

  showNameError(row, message) {
    const error = row.querySelector(".room-block-grid__name-error");
    if (error) {
      error.textContent = message;
      error.removeAttribute("hidden");
    }
    row.querySelector("[data-event-request-room-block-target='typeName']")?.setAttribute("aria-invalid", "true");
  }

  clearNameError(row) {
    row.querySelector(".room-block-grid__name-error")?.setAttribute("hidden", "");
    row.querySelector("[data-event-request-room-block-target='typeName']")?.removeAttribute("aria-invalid");
  }

  // A saved type is marked for destruction and hidden, and its nights left
  // out of the post (the type takes them with it); an unsaved one leaves the
  // DOM.
  removeType(event) {
    const row = event.target.closest("tr");
    if (!row || this.isStandardRow(row)) return;

    const id = row.querySelector(".room-block-grid__name-col input[name$='[id]']");
    if (id) {
      const destroy = row.querySelector(".room-block-grid__name-col input[name$='[_destroy]']");
      if (destroy) destroy.value = "1";
      this.rowCells(row).forEach((cell) => {
        cell.querySelectorAll("input").forEach((input) => {
          input.disabled = true;
        });
      });
      row.hidden = true;
    } else {
      row.remove();
    }

    this.renumberPositions();
    this.recalculate();
  }

  // The handle, from the keyboard: up and down move the row past its
  // neighbour. The blank row stays at the foot.
  moveByKey(event) {
    if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;

    const row = event.target.closest("tr");
    const rows = this.liveTypeRows();
    const at = rows.indexOf(row);
    if (at < 0) return;

    event.preventDefault();
    if (event.key === "ArrowUp" && at > 0) rows[at - 1].insertAdjacentElement("beforebegin", row);
    if (event.key === "ArrowDown" && at < rows.length - 1) rows[at + 1].insertAdjacentElement("afterend", row);

    this.renumberPositions();
    event.target.focus();
  }

  // The handle, from the mouse: a row is only draggable while its handle is
  // held, so text in its inputs can still be selected.
  armDrag(event) {
    const row = event.target.closest("tr");
    if (row && !row.classList.contains("room-block-grid__type--new")) row.draggable = true;
  }

  dragStart(event) {
    const row = event.target.closest?.("tr");
    if (!row?.draggable) return;

    this.dragging = row;
    row.classList.add("room-block-grid__type--dragging");
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", this.typeNameOf(row));
    }
  }

  dragOver(event) {
    const row = event.target.closest?.("tr");
    if (!this.dragging || !row || row === this.dragging || !this.liveTypeRows().includes(row)) return;

    event.preventDefault();
    const box = row.getBoundingClientRect();
    const below = event.clientY > box.top + box.height / 2;
    row.insertAdjacentElement(below ? "afterend" : "beforebegin", this.dragging);
  }

  drop(event) {
    if (this.dragging) event.preventDefault();
  }

  dragEnd() {
    if (!this.dragging) return;

    this.dragging.draggable = false;
    this.dragging.classList.remove("room-block-grid__type--dragging");
    this.dragging = null;
    this.renumberPositions();
  }

  // Position is the order the rows sit in, the blank row last.
  renumberPositions() {
    [...this.liveTypeRows(), ...this.newTypeRowTargets].forEach((row, position) => {
      const field = row.querySelector("[data-event-request-room-block-target='position']");
      if (field) field.value = String(position);
    });
  }

  // What screen readers call the row and its cells, once it has a name.
  nameRow(row, name) {
    row.querySelector(".room-block-grid__handle")
      ?.setAttribute("aria-label", `Reorder ${name}: drag, or press the up and down arrow keys`);
    row.querySelector(".room-block-grid__remove-type")?.setAttribute("aria-label", `Remove ${name}`);
    this.rowCells(row).forEach((cell) => {
      const label = cell.querySelector("label");
      // A function replacer, so a $' or $` in the name stays text (review of
      // !4698, finding 11).
      if (label) label.textContent = label.textContent.replace(/^.*? rooms on /, () => `${name} rooms on `);
    });
  }

  nextTypeIndex() {
    const rows = [...this.typeRowTargets, ...this.newTypeRowTargets];
    const highest = Math.max(-1, ...rows.map((row) => Number(row.dataset.index) || 0));
    this.typeCounter = Math.max(this.typeCounter ?? 0, highest + 1);
    return this.typeCounter++;
  }

  typeNameOf(row) {
    return row.querySelector("[data-event-request-room-block-target='typeName']")?.value.trim() ?? "";
  }

  liveTypeRows() {
    return this.typeRowTargets.filter((row) => !row.hidden);
  }

  // Every row that counts toward the figures. The pending row joins them
  // only once it is a real row (GR-3304 review).
  liveRows() {
    return this.liveTypeRows();
  }

  // Every row a new night needs a cell in, the pending row included. A
  // removed type takes its nights with it, so it needs none.
  allRows() {
    return [...this.liveTypeRows(), ...this.newTypeRowTargets];
  }

  rowCells(row) {
    return Array.from(row.querySelectorAll("[data-event-request-room-block-target='cell']"));
  }

  isStandardRow(row) {
    return row?.dataset.standard === "true";
  }

  isStandardCell(cell) {
    return this.isStandardRow(cell?.closest("tr"));
  }

  standardCells() {
    const row = this.typeRowTargets.find((each) => this.isStandardRow(each));
    return row ? this.rowCells(row).filter((cell) => !cell.hidden) : [];
  }

  countInput(cell) {
    return cell.querySelector("[data-event-request-room-block-target='count']");
  }

  // Clones a <template>'s one element with its placeholders filled in. The
  // longer placeholders go first, so __DATE__ never eats __DATE_LABEL__. The
  // typed name goes in through a function replacer: as a replacement string
  // its $' and $` would splice in the template around the match.
  fill(template, { index = "", date = "", typeIndex = "", typeName = "", position = "" }) {
    const html = template.innerHTML
      .replace(/__TYPE_INDEX__/g, String(typeIndex))
      .replace(/__TYPE_NAME__/g, () => this.escape(typeName))
      .replace(/__POSITION__/g, String(position))
      .replace(/__INDEX__/g, String(index))
      .replace(/__DATE_LABEL__/g, date ? this.dateLabel(date) : "")
      .replace(/__SUB_LABEL__/g, date ? this.subLabel(date) : "")
      .replace(/__DATE__/g, date);
    const holder = document.createElement("template");
    holder.innerHTML = html.trim();
    return holder.content.firstElementChild;
  }

  // A name typed for a type goes into markup and an attribute.
  escape(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML.replace(/"/g, "&quot;");
  }

  // The same rule EventRequests::RoomBlockStrip#sub_label_for carries: a night
  // is named for the day it follows, and the two ends of the block are named
  // for what they are. Stated twice because a cell added here has no server
  // render to take its wording from; the vitest pins that they agree.
  subLabel(date) {
    if (!this.firstDayValue || !this.lastDayValue) return "";

    const arrival = this.shiftDate(this.firstDayValue, -1);
    if (date < arrival) return "early arrival";
    if (date === arrival) return "arrival night";
    if (date > this.lastDayValue) return "extra night";

    return `night of day ${this.daysBetween(this.firstDayValue, date) + 1}`;
  }

  // Matches the server's "%a, %b %-d". Held in UTC throughout: these are plain
  // calendar dates, and parsing one in the viewer's zone is how a night ends
  // up labelled as the day before itself.
  dateLabel(date) {
    return this.asUtc(date).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
  }

  // Does a day of the meeting cover this night? The arrival night follows day
  // 1; anything before it, or past the final day, follows nothing. Same rule
  // as EventRequests::RoomBlockStrip#covered_day_date_for.
  //
  // GR-3448: the template's standard cell is custom, which is right for a
  // night that follows nothing; one a day still covers - the night of the
  // final day is the usual case - opens following that day instead.
  adoptCoveredDay(cell, date) {
    if (!cell) return;

    const covered = this.coveredDayFor(date);
    const capacity = covered === null ? null : this.dayCapacity(covered);
    if (capacity === null) return;

    cell.dataset.coversDay = covered;
    this.countInput(cell).value = capacity;
    this.markCustom(cell, false);
  }

  // The date of the day a night follows, or null where none of the meeting's
  // days covers it.
  coveredDayFor(date) {
    if (!this.firstDayValue || !this.lastDayValue) return null;

    const covered = date === this.shiftDate(this.firstDayValue, -1) ? this.firstDayValue : date;
    return covered >= this.firstDayValue && covered <= this.lastDayValue ? covered : null;
  }

  shiftDate(date, step) {
    const shifted = this.asUtc(date);
    shifted.setUTCDate(shifted.getUTCDate() + step);
    return shifted.toISOString().slice(0, 10);
  }

  daysBetween(from, to) {
    return Math.round((this.asUtc(to) - this.asUtc(from)) / 86400000);
  }

  asUtc(date) {
    const [year, month, day] = date.split("-").map(Number);
    return new Date(Date.UTC(year, month - 1, day));
  }
}
